import "./Project.scss";

import React, { FC, useEffect, useRef, useState } from "react";
import { useWindowSize } from "../../hooks/useWindowSize";
import { useInView } from "../../hooks/useInView";
import { useSwiperSlide } from "swiper/react";
import { Player } from "../Player";
import { CloseVideoIcon } from "../CloseVideoIcon";
import { t } from "i18next";

export interface HomeProject {
  slug?: string;
  title?: string;
  subTitle?: string;
  videoId: string;
  mobileVideoId?: string;
  order?: string;
  titleTag?: string;
  headerAnimated?: boolean;
  isDetail?: boolean;
  autoPlay?: boolean;
  customCursor?: boolean;
  defaultMuted?: boolean;
  isMobileAutoplayActive?: boolean;
  enableFullScreen?: boolean;
}

const Project: FC<HomeProject> = ({
  slug,
  title,
  subTitle,
  videoId,
  mobileVideoId,
  order,
  titleTag,
  headerAnimated,
  isDetail,
  autoPlay,
  customCursor,
  defaultMuted,
  isMobileAutoplayActive,
  enableFullScreen,
}) => {
  const titleContainer = useRef<HTMLHeadingElement>();

  const CustomTag = (titleTag ? titleTag : "h2") as keyof JSX.IntrinsicElements;

  const [windowWidth] = useWindowSize();
  const [isFullScreen, setIsFullScreen] = useState<boolean>(false);
  const [fontSize, setFontSize] = useState<string>();
  const swiperSlide = useSwiperSlide();

  const calculateFontSize = () => {
    if (!titleContainer.current || !refItem.current || isDetail) {
      return;
    }

    const fontSizeCalculated =
      (titleContainer.current.offsetWidth * 1.3) / title.length;
    setFontSize(`${fontSizeCalculated}px`);
  };

  useEffect(calculateFontSize, [title, windowWidth]);

  useEffect(() => {
    if (!swiperSlide?.isActive && isFullScreen) {
      setIsFullScreen(false);
    }
  }, [swiperSlide?.isActive]);

  const inViewConfiguration = {
    root: null,
    rootMargin: "0px",
    threshold: 0.2,
  };
  const refItem = useRef<HTMLDivElement>();
  const isInView = useInView(refItem, inViewConfiguration, true);

  const description = (order, subTitle, fontSize) => {
    return order || subTitle ? (
      <p
        className="home-project__brand"
        style={{ "--line-geight": fontSize } as React.CSSProperties}
      >
        ({order}) — {subTitle}
      </p>
    ) : (
      ""
    );
  };
  function toggleFullScreen() {
    if (!enableFullScreen) {
      return;
    }

    setIsFullScreen(!isFullScreen);
  }

  return (
    <article
      ref={refItem}
      className={`home-project ${isDetail ? "home-project--detail" : ""} ${
        isInView ? "home-project--in-view" : ""
      }`}
    >
      {title ? (
        <header
          className={`home-project__header ${
            headerAnimated ? "home-project__header--animated" : ""
          }`}
        >
          <div ref={titleContainer}>
            <CustomTag className="home-project__title" style={{ fontSize }}>
              {description(order, subTitle, fontSize)}
              {slug ? <a href={slug}>{title}</a> : <span>{title}</span>}
            </CustomTag>
          </div>
        </header>
      ) : (
        ""
      )}

      <div
        className={`home-project__video ${customCursor ? "cursor-click" : ""} ${
          isDetail ? "is-detail" : ""
        } ${isFullScreen ? "is-fullscreen" : ""}`}
      >
        <Player
          alwaysMute={isDetail ? defaultMuted : true}
          autoPlay={autoPlay}
          isMobileAutoplayActive={isMobileAutoplayActive}
          videoId={videoId}
          mobileVideoId={mobileVideoId}
          showMobileVideo={isDetail}
          isFullScreen={isFullScreen}
        />
        {isDetail && !defaultMuted && (
          <>
            <CloseVideoIcon
              isFullScreen={isFullScreen}
              onClickClose={() => toggleFullScreen()}
            />
            <div
              className="home-project__video-overlay cursor-click"
              data-cursor-text={isFullScreen ? " " : t("common.play")}
              data-cursor-rotate-text={t(
                isFullScreen ? "common.thisIsYouPlanet" : "common.fullScreen"
              )}
              onClick={toggleFullScreen}
            />
          </>
        )}
      </div>
    </article>
  );
};

export default Project;
